import React, { MouseEventHandler, PropsWithChildren } from 'react';
import styled from 'styled-components';
import componentProps, { ComponentProps } from '../../helpers/componentProps';
import { applyComponentTheme } from '../../theme';
import {
  actionLinkStyle,
  baseLinkStyle,
  blockLinkStyle,
  ButtonStyledAsLink,
  inlineTextLinkStyle,
  StyledFakeLink,
  StyledLink,
  ThemeColor,
} from './styles';

type BaseLinkProps = {
  onClick?: MouseEventHandler;
  color?: ThemeColor | string;
  tabIndex?: number;
  disabled?: boolean;
  children?: React.ReactNode;
} & ComponentProps;

export const FakeLink: React.FC<BaseLinkProps> = ({
  onClick,
  children,
  color,
  tabIndex = 0,
  disabled,
  ...props
}) => (
  <StyledFakeLink
    {...componentProps(props)}
    onClick={disabled ? undefined : onClick}
    mainColor={color}
    tabIndex={tabIndex}>
    {children}
  </StyledFakeLink>
);

type LinkProps = BaseLinkProps & {
  href: string;
  removeVisitedStyle?: boolean;
  openInNewTab?: boolean;
};

const Link: React.FC<LinkProps> = ({
  href,
  onClick,
  children,
  color,
  removeVisitedStyle,
  openInNewTab,
  tabIndex = 0,
  disabled,
  ...props
}) => (
  <StyledLink
    {...componentProps(props)}
    href={disabled ? '' : href}
    onClick={disabled ? undefined : onClick}
    mainColor={color}
    removeVisitedStyle={removeVisitedStyle}
    target={openInNewTab ? '_blank' : undefined}
    tabIndex={tabIndex}>
    {children}
  </StyledLink>
);

type ButtonLinkProps = {
  onClick: MouseEventHandler;
  color?: ThemeColor | string;
  tabIndex?: number;
  disabled?: boolean;
} & ComponentProps;

export const ButtonLink = applyComponentTheme<PropsWithChildren<ButtonLinkProps>>(
  ({ onClick, color, children, tabIndex, disabled, ...props }) => (
    <ButtonStyledAsLink
      type="button"
      {...componentProps(props)}
      onClick={disabled ? undefined : onClick}
      mainColor={color}
      tabIndex={tabIndex}
      disabled={disabled}>
      {children}
    </ButtonStyledAsLink>
  ),
  'ButtonLink'
);

const ActionLink = styled(ButtonLink)`
  ${actionLinkStyle}
`;

const ActionAnchor = styled(Link)`
  ${actionLinkStyle}
`;

export default applyComponentTheme(Link, 'Link');
export { ActionLink, ActionAnchor, baseLinkStyle, blockLinkStyle, inlineTextLinkStyle };
